import * as React from 'react'

import { VehicleLockModel } from '@/app/models/vehicle/vehicleLockModel'
import useVehicleService from '../../../services/VehicleService'
import moment from 'moment'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { getVehicleLocksColumns } from '@/modules/vehicles/constants/tables'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useVehicles } from '@/modules/shared/context/vehicles/VehiclesContext'
import { useSideVehicleSelection } from '@/modules/vehicles/context/SideVehicleSelectionContext'

type LockWithVehicle = VehicleLockModel & { registrationNumber: string }

interface GroupProps {
  companyId: string
}

const BlocksGroup: React.FC<GroupProps> = ({ companyId }) => {
  const {sideCheckedIds} = useSideVehicleSelection()
  const { getVehicleLocks } = useVehicleService()
  const { t } = useLocalize()
  const { allVehicles } = useVehicles()

  const [locks, setLocks] = React.useState<LockWithVehicle[]>([])
  const [loading, setLoading] = React.useState(false)

  const locksCache = React.useRef<Map<string, LockWithVehicle[]>>(new Map())
  const prevCompanyId = React.useRef<string>(companyId)

  const VehicleLocksColumns = React.useMemo(
    () => [
      {
        title: t('vehicleBasics.registration-number'),
        key: null,
        type: 'string' as const,
        sort: false,
      },
      ...getVehicleLocksColumns(t),
    ],
    [t],
  )

  React.useEffect(() => {
    if (prevCompanyId.current !== companyId) {
      locksCache.current.clear()
      prevCompanyId.current = companyId
    }

    if (sideCheckedIds.length === 0) {
      setLocks([])
      return
    }

    const newIds = sideCheckedIds.filter((id) => !locksCache.current.has(id))
    const computeLocks = () =>
      sideCheckedIds.flatMap((id) => locksCache.current.get(id) ?? [])

    if (newIds.length === 0) {
      setLocks(computeLocks())
      return
    }

    const fetchLocks = async () => {
      setLoading(true)
      try {
        await Promise.all(
          newIds.map(async (id) => {
            const vehicle = allVehicles.find((v) => v.id === id)
            const lockData: VehicleLockModel[] = await getVehicleLocks({
              companyId,
              vehicleId: String(id),
            })
            locksCache.current.set(
              id,
              lockData.map((lock) => ({
                ...lock,
                registrationNumber:
                  lock.registrationNumber ?? vehicle?.registrationNumber ?? '—',
              })),
            )
          }),
        )
        setLocks(computeLocks())
      } finally {
        setLoading(false)
      }
    }

    fetchLocks()
  }, [sideCheckedIds, companyId])

  const formatDate = (value: any) => {
    if (!value) return '—'
    const m = moment(value)
    return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : '—'
  }

  return (
    <>
      <div className="tw-z-10 tw-flex-1 tw-overflow-y-auto  ">

        {sideCheckedIds.length === 0 && (
          <div className="tw-grid tw-place-items-center tw-px-4 tw-py-12 tw-text-center tw-text-sm tw-text-content-secondary">
            {t('vehicleModule.select-vehicles-side')}
          </div>
        )}

        {sideCheckedIds.length > 0 && (
          <div className="tw-border-t tw-border-gray-200">
            {loading ? (
              <div className="tw-grid tw-place-items-center tw-py-8">
                <LoaderIcon className="tw-w-16 tw-animate-spin" />
              </div>
            ) : locks.length > 0 ? (
              <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
                <TableHeader
                  opacity
                  rounded={false}
                  toggleAll={() => {}}
                  isSelectedAny={false}
                  columns={VehicleLocksColumns}
                  sortConfig={null}
                  onHeaderClick={undefined}
                  disabledToggle
                  disabledCheckboxToggle={true}
                />
                <tbody>
                  {locks.map((lock, index) => (
                    <tr
                      key={index}
                      className="notches-border--right tw-w-fit tw-border-b tw-bg-white"
                    >
                      <td className="tw-py-3.5 tw-pl-2.5 tw-pr-3" />
                      <TableTextRow bolder={false}>
                        {lock.registrationNumber ?? '-'}
                      </TableTextRow>
                      <TableTextRow bolder={false}>
                        {formatDate(lock.lockBeginTime)}
                      </TableTextRow>
                      <TableTextRow bolder={false}>
                        {formatDate(lock.lockEndTime)}
                      </TableTextRow>
                      <TableTextRow bolder={false}>
                        {lock.lockingCompany || '—'}
                      </TableTextRow>
                      <TableTextRow bolder={false}>
                        {lock.lockingCardNumber || '—'}
                      </TableTextRow>
                      <TableTextRow bolder={false}>
                        {lock.vehicleIdentificationId ?? '—'}
                      </TableTextRow>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <>
                <TableHeader
                  opacity
                  toggleAll={() => {}}
                  isSelectedAny={false}
                  columns={VehicleLocksColumns}
                  sortConfig={null}
                  disabledToggle
                  disabledCheckboxToggle={true}
                />
                <TableEmpty title={t('vehicleModule.no-locks')} />
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default BlocksGroup
